import { Component, Input, OnInit } from '@angular/core';
import { IRate } from '@core/models/Rate/Rate.interface';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.reducer';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HotelAvailability } from '@core/models/HotelAvailability/HotelAvailability.model';
import { selectHotelAvailability } from '@store/hotel-availability/hotel-availability.selectors';
import {
  DisneyService,
  PriceService,
  ReqService,
  RoomDetailsService,
  SearchStateService,
} from '@core/services';
import { IHotelStaticInfo } from '@core/models/HotelStaticInfo/hotelStaticInfo.interface';
import { RoomPrice } from '../../../../../shared/enums/room-price.enum';
import {
  IconBadge,
  PaymentMethod,
} from '@core/services/room-details/room-details.service';
import { MealPlan } from '@core/models/MealPlan/meal-plan.enum';
import { DisneyRoomCluster } from '@core/models/disney-room-cluster/DisneyRoomCluster.model';
import { DisneyRoomType } from '../../../../../shared/enums/disney-room-type.enum';
import { PrepaidRoomCluster } from '@core/models/PrepaidRoomCluster/PrepaidRoomCluster.model';
import { RateService } from '@core/services/rate-service/rate.service';
import { take } from 'rxjs/operators';
import { RateStatus } from '../../../../../shared/enums/rate-status.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { disneyTickets } from '@store/disney-tickets/disney-tickets.actions';
import { DISNEY_FREE_MEAL_PLAN_CODES } from '../../../../../shared/constants/disney-offer-codes';
import { TotalPriceComponent } from '../../detail-cluster/total-price/total-price.component';
import { PriceComponent } from '@commons/price/price.component';
import { DisneyCombosComponent } from '../../disney-combos/disney-combos.component';
import { ChargesComponent } from '../../charges/charges.component';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MbIconText, MbIcon, MbButton } from '@am/moebius';

@Component({
    selector: 'am-rate-detail',
    templateUrl: './rate-detail.component.html',
    styleUrls: ['./rate-detail.component.scss'],
    standalone: true,
    imports: [
        MbIconText,
        MbIcon,
        ChargesComponent,
        DisneyCombosComponent,
        PriceComponent,
        MbButton,
        TotalPriceComponent,
        AsyncPipe,
        DatePipe,
    ],
})
export class RateDetailComponent implements OnInit {
  public readonly RATE_STATUS = RateStatus;
  private _rate: IRate;
  private _disneyCluster: DisneyRoomCluster;
  public hotelAvailability$: Observable<HotelAvailability>;
  public staticInfo: IHotelStaticInfo;
  public priceBy: RoomPrice;
  public mealPlan: IconBadge<MealPlan>;
  public paymentMethod: IconBadge<PaymentMethod>;
  private _loading$: Subject<boolean> = new BehaviorSubject(false);
  public isDisney: boolean;
  public disneyRoomType: DisneyRoomType;
  public guests: number;
  public nights: number;
  public rooms: number;

  constructor(
    private store: Store<AppState>,
    private readonly contextService: ReqService,
    private readonly roomDetailsService: RoomDetailsService,
    private readonly rateService: RateService,
    private readonly searchStateService: SearchStateService,
    private readonly priceService: PriceService,
    private readonly disneyService: DisneyService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {
    this.hotelAvailability$ = this.store.select(selectHotelAvailability);
    this.isDisney = this.contextService.isDisneyHotel();
    if (this.isDisney) {
      const decodedRooms = this.searchStateService.getDecodedRooms();
      this.nights = this.priceService.totalNights;
      this.rooms = this.priceService.totalRooms;
      this.guests = decodedRooms.total;
    }
  }

  @Input()
  public elevation: number;

  @Input()
  set rate(value: IRate) {
    this._rate = value;
    this.paymentMethod = this.roomDetailsService
      .getPaymentMethods([value], this.contextService.getHotelStaticInfo().country)
      .pop();
    this.mealPlan = this.roomDetailsService.getMealPlans([value]).pop();
  }

  @Input()
  set disneyCluster(value: PrepaidRoomCluster) {
    const disneyCluster = value as DisneyRoomCluster;
    this._rate = disneyCluster.rates[0];
    this._disneyCluster = disneyCluster;
    this.paymentMethod = this.roomDetailsService
      .getPaymentMethods([this._rate], this.contextService.getHotelStaticInfo().country)
      .pop();
  }

  ngOnInit(): void {
    this.staticInfo = this.contextService.getHotelStaticInfo();
    this.priceBy = this.contextService.context.params.priceBy;
  }

  public bookRate() {
    this._loading$.next(true);
    if (DisneyRoomCluster.onlyRoomOption(this._disneyCluster.rates)) {
      this.rateService
        .bookReservation(this.rate as any)
        .pipe(take(1))
        .subscribe((res) => {
          this._rate = res;
          this._loading$.next(false);
        });
    } else {
      this.store.dispatch(
        disneyTickets({
          disneyTicketCluster: this.disneyService.createTicketsCluster(
            this._disneyCluster,
          ),
        }),
      );
      this.redirectTickets();
      this._loading$.next(false);
    }
  }

  get rate(): IRate {
    return this._rate;
  }

  get hasMealPlans(): boolean {
    return (this._disneyCluster as any).hasMealPlans;
  }

  get offerCode(): string {
    return (this._disneyCluster?.rooms[0] as any)?.offerCode;
  }

  get isFreeMealPlan(): boolean {
    return DISNEY_FREE_MEAL_PLAN_CODES.includes(this.offerCode);
  }

  get hasTickets(): boolean {
    return (this._disneyCluster as any).hasTickets;
  }

  get hasRoomOnlyOption(): boolean {
    return (this._disneyCluster as any).hasRoomOnlyOption;
  }

  get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  private redirectTickets() {
    const url = 'hotels/add-ons';
    const hotel = this.activatedRoute.snapshot.params.query;
    const navigateParams = this.activatedRoute.snapshot.queryParams;
    this.router.navigate([`${url + '/' + hotel}`], { queryParams: navigateParams });
  }
}
